<template>
  <div class="flex h-full w-full flex-col py-4">
    <div
      class="border-ultra mb-6 flex w-full rounded-xl border-0 ring ring-green-300/10"
    >
      <section class="flex flex-col">
        <div class="grow">
          <div class="mt-0 flex flex-col p-4">
            <div class="-mt-1 mb-2 inline-flex items-center space-x-2 py-0">
              <IconUltra class="h-6 w-6" />
              <span class="mt-1 text-xl font-medium tracking-wide">
                Streamlabs Ultra
              </span>
            </div>
            <div class="flex w-full justify-between">
              <span class="text-sm">
                Unlock powerful tools to help you grow faster and stand out from
                the crowd.
              </span>
              <span class="text-sm">From 27$/mo</span>
            </div>
            <div class="flex space-x-6 py-4">
              <div class="flex w-1/3 flex-col">
                <img
                  src="https://cdn.streamlabs.com/static/imgs/onboarding/onboarding-multistream.png"
                >
                <span class="my-4 font-medium">Multistream everywhere</span>
                <p class="text-sm">
                  Go live on Twitch, YouTube, Kick, and more at the same time to
                  reach a wider audience. Our servers do the work so your PC can
                  stream.
                </p>
              </div>
              <div class="flex w-1/3 flex-col">
                <img
                  src="https://cdn.streamlabs.com/static/imgs/onboarding/onboarding-premium-overlays.png"
                >
                <span class="my-4 font-medium">Premium stream overlays</span>
                <p class="text-sm">
                  Create a stream that feels uniquely yours with thousands of
                  premium overlays.
                </p>
              </div>
              <div class="flex w-1/3 flex-col">
                <img
                  src="https://cdn.streamlabs.com/static/imgs/onboarding/onboarding-widgetthemes.png"
                >
                <span class="my-4 font-medium">Widget themes</span>
                <p class="text-sm">
                  Choose from a wide variety of widget themes to enhance your
                  broadcast.
                </p>
              </div>
            </div>
            <div class="flex flex-col items-center justify-center">
              <Button
                class="rounded-full px-24"
                size="lg"
                variant="ultra"
                @click="onUltraButtonClick"
              >
                Join Ultra</Button
              >

              <a
                :href="streamlabsUltraBrowseUrl"
                @click.prevent="onStreamlabsUltraBrowse"
                class="mt-2 text-sm text-muted-foreground underline hover:text-white"
              >
                Or explore dozens more Ultra benefits
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
    <div>
      <Card class="flex p-4 ring ring-1 ring-white">
        <div class="flex w-2/3 flex-col">
          <div class="-mt-1 inline-flex items-center space-x-2 py-2">
            <IconStreamlabs class="h-6 w-6" />
            <span class="mt-1 text-xl font-medium tracking-wide">Free</span>
          </div>
          <span class="text-sm"
            >Everything you need to stream for free. You can upgrade to Ultra at
            any time.
          </span>
        </div>
        <div class="flex w-1/3 items-center justify-end">
          <div class="flex flex-col">
            <div
              class="mb-4 flex items-center gap-2 rounded-sm bg-transparent px-1"
              :style="{
                color: '#80F5D2',
                'background-color': 'rgba(128,245,210,0.08)',
              }"
            >
              <Icon name="material-symbols:check-circle" />
              <span>Selected</span>
            </div>
            <span class="text-medium">Free, forever.</span>
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconCloudbot from '~/components/icons/IconCloudbot.vue';
import IconMultistream from '~/components/icons/IconMultistream.vue';
import IconOverlays from '~/components/icons/IconOverlays.vue';
import IconTipping from '~/components/icons/IconTipping.vue';
import IconUltra from '~/components/icons/IconUltra.vue';
import IconWidgets from '~/components/icons/IconWidgets.vue';

import { openBrowserTo } from '~/lib/bindings';
import { getUltraUrl } from '~/lib/ultra';

const { track: $track } = useTrackingStore();

const emit = defineEmits(['complete']);

const onUltraButtonClick = () => {
  $track('onboarding_ultra');
  openBrowserTo(getUltraUrl({ refl: 'obsplugin-onboarding' }));
  emit('complete');
};

const streamlabsUltraBrowseUrl = getUltraUrl(
  { refl: 'obsplugin-onboarding' },
  false,
);

const onStreamlabsUltraBrowse = () => openBrowserTo(streamlabsUltraBrowseUrl);

// TODO: cleanup below

const plans = [
  {
    label: 'Starter',
    price: 'Free',
    description: 'Everything you need to go live. Always and forever free.',
    onClick: () => {
      $track('onboarding_free');
      emit('complete');
    },
  },
  {
    label: 'Ultra',
    price: '$27',
    per: 'month',
    description: 'One plan, all the premium features',
    onClick: () => {},
  },
];

const benefits = [
  {
    label: 'Group 1',
    items: [
      {
        label: 'Overlays',
        values: ['Free Only', 'Premium Library'],
        icon: {
          component: IconOverlays,
          class: 'h-5 w-5 opacity-70',
        },
      },

      {
        label: 'Widget Themes',
        icon: {
          name: 'fa6-solid:brush',
          size: '1.25rem',
          class: 'opacity-70',
        },
        values: ['Free Only', 'Premium Library'],
      },
      {
        label: 'Accept tips',
        icon: {
          component: IconTipping,
          class: 'w-5 h-5 opacity-70',
        },
        values: [true, true],
      },
      {
        label: 'Cloudbot',
        icon: {
          component: IconCloudbot,
          class: 'w-5 h-5 opacity-70',
        },
        values: [true, true],
      },
      {
        label: 'Alerts & Widgets',
        icon: {
          component: IconWidgets,
          class: 'w-5 h-5 opacity-70',
        },
        values: [true, true],
      },

      {
        label: 'Multistream',
        values: [false, true],
        icon: {
          component: IconMultistream,
          class: 'h-5 w-5 opacity-70',
        },
      },
      {
        label: 'More premium tools',
        values: [false, true],
        icon: {
          component: IconUltra,
          class: 'h-5 w-5 opacity-70',
        },
      },

      //   {
      //     label: "Free Overlays",
      //     values: [true, true],
      //   },
      //   {
      //     label: "Premium Overlays",
      //     values: [false, true],
      //   },
      //   {
      //     label: "Multistream",
      //     values: [false, true],
      //   },
      //   {
      //     label: "More premium tools",
      //     values: [false, true],
      //   },
    ],
  },
  //   {
  //     label: "Group 2",
  //     items: [
  //       {
  //         label: "Perk #1",
  //         values: [400, 800, "Unlimited"],
  //       },
  //       {
  //         label: "Perk #2",
  //         values: [400, 800, "Unlimited"],
  //       },
  //     ],
  //   },
];

const totalBenefitsCount = computed(() => {
  return benefits.reduce((c, { items }) => c + items.length, 0);
});
</script>

<style lang="scss" scoped>
section {
  //   @apply md:contents [&>div:first-child]:rounded-t-2xl [&>div:first-child]:pt-10 [&>div:last-child]:rounded-b-2xl [&>div:last-child]:pb-10;
  @apply md:contents [&>div:first-child]:rounded-t-2xl [&>div:last-child]:rounded-b-2xl;
}
</style>
